import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./GenerativeAi.css";
import Testimonial from "../../../Testimonial/Testimonial";

const GenerativeAi = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);


    const [activeFeature, setActiveFeature] = useState(0);

const features = [
  {
    title: "AI-Powered Content Generation",
    description:
      "Create high-quality, original content for your business needs with our advanced language models.",
    icon: "📝",
  },
  {
    title: "Image Synthesis & Creation",
    description:
      "Generate stunning visuals, artwork, and designs from simple text descriptions.",
    icon: "🎨",
  },
  {
    title: "Predictive Analytics",
    description:
      "Leverage AI to forecast trends, customer behavior, and market dynamics with precision.",
    icon: "📊",
  },
  {
    title: "Intelligent Automation",
    description:
      "Streamline workflows and processes with AI-driven automation solutions.",
    icon: "⚙️",
  },
  {
    title: "Conversational AI & Chatbots",
    description:
      "Enhance customer engagement with natural, human-like conversations powered by AI chatbots.",
    icon: "💬",
  },
  {
    title: "Natural Language Processing (NLP)",
    description:
      "Extract insights, sentiments, and meaning from text to enhance decision-making.",
    icon: "🔍",
  },
  {
    title: "Computer Vision",
    description:
      "Leverage AI to analyze images, videos, and real-world environments with high accuracy.",
    icon: "📷",
  },
  {
    title: "Recommendation Systems",
    description:
      "Personalize user experiences with intelligent recommendation engines like Netflix & Amazon.",
    icon: "⭐",
  },
  {
    title: "Fraud Detection & Risk Analysis",
    description:
      "Use AI models to detect anomalies, prevent fraud, and ensure secure transactions.",
    icon: "🛡️",
  },
  {
    title: "Speech Recognition & Voice AI",
    description:
      "Convert speech to text, build voice assistants, and enable hands-free operations.",
    icon: "🎤",
  },
  {
    title: "Generative Design",
    description:
      "Automate product and architecture design with AI-driven creative solutions.",
    icon: "🏗️",
  },
  {
    title: "Digital Twins",
    description:
      "Simulate real-world systems and assets with AI-powered digital twin technology.",
    icon: "🌐",
  },
  {
    title: "Emotion AI",
    description:
      "Analyze tone, sentiment, and emotions to improve customer engagement and insights.",
    icon: "😊",
  },
  {
    title: "Autonomous Systems",
    description:
      "AI-driven robotics and autonomous systems for logistics, drones, and vehicles.",
    icon: "🚗",
  },
  {
    title: "Healthcare AI",
    description:
      "AI-powered diagnostics, medical imaging, and personalized treatment recommendations.",
    icon: "🏥",
  },
  {
    title: "Cybersecurity AI",
    description:
      "Protect digital assets with AI-driven threat detection, prevention, and real-time response.",
    icon: "🔒",
  },
];


const services = [
  {
    title: "Custom AI Solutions",
    description:
      "Tailor-made generative AI systems designed for your specific business requirements.",
  },
  {
    title: "AI Integration Services",
    description:
      "Seamlessly integrate cutting-edge AI capabilities into your existing systems.",
  },
  {
    title: "AI Consulting",
    description:
      "Expert guidance on implementing and maximizing the value of generative AI in your organization.",
  },
  {
    title: "Training & Support",
    description:
      "Comprehensive training programs and ongoing support for your team.",
  },
  {
    title: "AI Model Development",
    description:
      "Design, train, and fine-tune custom AI/ML models optimized for your business use cases.",
  },
  {
    title: "AI Strategy & Roadmap",
    description:
      "Develop a clear AI adoption strategy aligned with your long-term business goals.",
  },
  {
    title: "Edge AI Solutions",
    description:
      "Deploy AI models at the edge for faster processing and real-time decision-making.",
  },
  {
    title: "Data Engineering & Preparation",
    description:
      "Ensure clean, structured, and high-quality datasets to fuel AI success.",
  },
  {
    title: "AI-as-a-Service (AIaaS)",
    description:
      "Flexible, cloud-based AI solutions available on-demand for rapid scaling.",
  },
  {
    title: "AI Governance & Ethics",
    description:
      "Implement responsible AI frameworks to ensure transparency, fairness, and compliance.",
  },
  {
    title: "MLOps & AI Deployment",
    description:
      "End-to-end pipelines for training, deploying, and maintaining AI/ML models at scale.",
  },
  {
    title: "AI Cloud Infrastructure",
    description:
      "Cloud-native AI platforms for scalable training, inference, and analytics.",
  },
  {
    title: "AI Security Solutions",
    description:
      "Protect AI models and data pipelines from threats with advanced security frameworks.",
  },
  {
    title: "Explainable AI (XAI)",
    description:
      "Make AI models transparent, interpretable, and trustworthy for business and compliance.",
  },
  {
    title: "Multimodal AI Solutions",
    description:
      "Leverage AI that combines text, image, audio, and video for holistic intelligence.",
  },
  {
    title: "Industry-Specific AI",
    description:
      "Vertical-focused AI solutions for healthcare, fintech, retail, logistics, and more.",
  },
];

  return (
    <div>
      <Helmet>
        <title>Generative AI Solutions | Advanced AI Technology Stack</title>
        <meta 
          name="description" 
          content="Explore our cutting-edge Generative AI solutions powered by the latest artificial intelligence technologies and frameworks." 
        />
        <meta 
          name="keywords" 
          content="Generative AI, artificial intelligence, AI solutions, machine learning, deep learning, neural networks, AI models, natural language processing, computer vision" 
        />
      </Helmet>
      
          <div className="genai-page-container">
      {/* Hero Section */}
      <section className="genai-hero-section">
        <div className="genai-hero-content">
          <h1 className="genai-main-heading">Generative AI Solutions</h1>
          <p className="genai-hero-text">
            Transform your business with our cutting-edge generative AI technologies. 
            Create, innovate, and automate like never before.
          </p>
          <a href="/contact-us"><button className="genai-cta-button">Explore Possibilities</button></a>
        </div>
        <div className="genai-hero-visual">
          <div className="genai-animated-orb"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="genai-features-section">
        <h2 className="genai-section-title">Our AI Capabilities</h2>
        <div className="genai-features-grid">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`genai-feature-card ${activeFeature === index ? 'genai-feature-active' : ''}`}
              onMouseEnter={() => setActiveFeature(index)}
            >
              <div className="genai-feature-icon">{feature.icon}</div>
              <h3 className="genai-feature-title">{feature.title}</h3>
              <p className="genai-feature-desc">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="genai-services-section">
        <h2 className="genai-section-title">Our AI Services</h2>
        <div className="genai-services-container">
          {services.map((service, index) => (
            <div key={index} className="genai-service-item">
              <h3 className="genai-service-title">{service.title}</h3>
              <p className="genai-service-desc">{service.description}</p>
              <div className="genai-service-hover-effect"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="genai-benefits-section">
  <h2 className="genai-section-title">Why Choose Our AI Solutions</h2>
  <div className="genai-benefits-list">
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🚀</div>
      <h3>Increased Efficiency</h3>
      <p>Automate repetitive tasks and accelerate workflows</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">💡</div>
      <h3>Enhanced Creativity</h3>
      <p>Unlock new possibilities for innovation and content creation</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">📈</div>
      <h3>Data-Driven Insights</h3>
      <p>Transform data into actionable intelligence</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🛡️</div>
      <h3>Secure & Ethical</h3>
      <p>Responsible AI implementation with privacy protection</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">⚡</div>
      <h3>Scalability</h3>
      <p>Seamlessly scale AI solutions as your business grows</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🌍</div>
      <h3>Global Accessibility</h3>
      <p>Leverage AI solutions accessible anytime, anywhere</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🤝</div>
      <h3>Seamless Integration</h3>
      <p>Integrates smoothly with your existing tools and workflows</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🎯</div>
      <h3>Improved Accuracy</h3>
      <p>Minimize errors with AI-powered precision and reliability</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">⏳</div>
      <h3>Time Savings</h3>
      <p>Reduce manual work and save valuable time for teams</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🔄</div>
      <h3>Continuous Learning</h3>
      <p>AI models that adapt and improve with real-world data</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">📊</div>
      <h3>Better Decision-Making</h3>
      <p>Support strategic choices with AI-driven recommendations</p>
    </div>
    <div className="genai-benefit-item">
      <div className="genai-benefit-icon">🌱</div>
      <h3>Sustainability</h3>
      <p>Optimize resources and reduce environmental impact with smart AI solutions</p>
    </div>
  </div>
</section>


      {/* CTA Section */}
      <section className="genai-cta-section">
        <h2 className="genai-cta-title">Ready to Transform Your Business with AI?</h2>
        <p className="genai-cta-text">
          Contact us today to discuss how our generative AI solutions can drive innovation and growth for your organization.
        </p>
        <a
  href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20Generative%20AI%20solutions%20such%20as%20chatbots%2C%20content%20automation%2C%20and%20AI-driven%20applications.%20Please%20connect%20me%20with%20your%20experts%20to%20get%20started."
  target="_blank"
  rel="noreferrer"
>
  <button className="genai-cta-button genai-cta-large">Get Started Now</button>
</a>

      </section>
    </div>

      
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerativeAi;